"use client"
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft, Loader2, Save, X } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';

interface MediaItem {
  filename: string;
  originalName: string;
  path: string;
  url: string;
  type: 'image' | 'video' | 'document';
  mimeType: string;
  size: number;
  dimensions?: {
    width: number;
    height: number;
  };
  title?: string;
  caption?: string;
  description?: string;
  altText?: string;
  tags?: string[];
  usedIn: {
    type: 'blog' | 'service' | 'page';
    id: string;
    title: string;
    field?: string;
  }[];
  uploadedAt: string | Date;
}

interface MediaEditorProps {
  mediaItem: MediaItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  onBack?: () => void;
}

export default function MediaEditor({ mediaItem, isOpen, onClose, onSave, onBack }: MediaEditorProps) {
  const [formData, setFormData] = useState({
    filename: '',
    title: '',
    caption: '',
    description: '',
    altText: '',
    tags: [] as string[]
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>('');

  useEffect(() => {
    if (mediaItem) {
       
      setFormData({
        filename: mediaItem.filename,
        title: mediaItem.title || '',
        caption: mediaItem.caption || '',
        description: mediaItem.description || '',
        altText: mediaItem.altText || '',
        tags: mediaItem.tags || []
      });
      setError(null);
      setImageError(false);
      // Ensure URL is properly formatted
      if (mediaItem.url) {
        // Use the URL directly if it's already a valid path
        // Otherwise construct it based on the URL pattern
        let url = mediaItem.url;
        if (!url.startsWith('/') && !url.startsWith('http')) {
          // If it's just a filename, determine the directory
          if (mediaItem.path) {
            if (mediaItem.path.includes('testimonials')) {
              url = `/uploads/testimonials/${mediaItem.filename}`;
            } else if (mediaItem.path.includes('service-meta')) {
              url = `/uploads/service-meta/${mediaItem.filename}`;
            } else {
              url = `/uploads/blogs/${mediaItem.filename}`;
            }
          } else {
            url = `/uploads/blogs/${mediaItem.filename}`;
          }
        }
        setImageUrl(url);
      } else {
        setImageUrl(`/uploads/blogs/${mediaItem.filename}`);
      }
    }
  }, [mediaItem]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };


  const handleSave = async () => {
    if (!mediaItem) return;

    setSaving(true);
    setError(null);

    try {
      const updateData: {
        title?: string;
        caption?: string;
        description?: string;
        altText?: string;
        tags?: string[];
        newFilename?: string;
        currentUrl?: string;
        currentPath?: string;
      } = {
        currentUrl: mediaItem.url,
        currentPath: mediaItem.path
      };

      if (formData.title) updateData.title = formData.title;
      if (formData.caption) updateData.caption = formData.caption;
      if (formData.description) updateData.description = formData.description;
      if (formData.altText) updateData.altText = formData.altText;
      if (formData.tags.length > 0) updateData.tags = formData.tags;

      // Only include newFilename if it's different
      if (formData.filename !== mediaItem.filename) {
        updateData.newFilename = formData.filename;
      }

      const encodedFilename = encodeURIComponent(mediaItem.filename);
      const response = await fetch(`/api/admin/media/${encodedFilename}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update media file');
      }

      onSave();
      // Use onBack if available (it will handle reopening the media modal)
      // Otherwise use onClose
      if (onBack) {
        onBack();
      } else {
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (!mediaItem) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/16 backdrop-blur-md bg-opacity-50 z-[200]" />
        <Dialog.Content className="fixed inset-0 z-[201] flex items-center justify-center p-4">
          <div
            className="bg-white rounded-md shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto transition-all duration-200 ease-out"
            style={{
              opacity: isOpen ? 1 : 0,
              transform: isOpen ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(-10px)',
            }}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {onBack && (
                    <button
                      onClick={onBack}
                      className="text-gray-600 hover:text-gray-900 transition-colors p-1.5 rounded-md hover:bg-gray-100"
                      title="Back to media list"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                  )}
                  <Dialog.Title className="text-xl font-semibold text-gray-900">
                    Edit Media
                  </Dialog.Title>
                </div>
                <Dialog.Close asChild>
                  <button className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-md hover:bg-gray-100">
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Preview */}
              <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
                {mediaItem.type === 'image' ? (
                  imageError ? (
                    <div className="text-center space-y-2">
                      <div className="text-red-600 font-semibold text-lg mb-2">
                        Media file not found
                      </div>
                      <p className="text-sm text-gray-600">
                        The file <code className="bg-gray-200 px-2 py-1 rounded text-xs">{mediaItem.filename}</code> could not be loaded.
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Expected path: <code className="bg-gray-200 px-2 py-1 rounded">{mediaItem.url || mediaItem.path || mediaItem.filename}</code>
                      </p>
                      <button
                        onClick={() => {
                          setImageError(false);
                          setImageUrl(`${mediaItem.url || imageUrl}?t=${Date.now()}`);
                        }}
                        className="mt-3 px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary/90 transition-colors"
                      >
                        Retry Loading
                      </button>
                    </div>
                  ) : (
                    <Image
                      src={imageUrl}
                      alt={mediaItem.altText || mediaItem.filename}
                      width={800}
                      height={512}
                      unoptimized
                      className="max-h-64 object-contain rounded w-auto h-auto"
                      onError={() => {
                        setImageError(true);
                        console.error('Failed to load image:', imageUrl);
                      }}
                      onLoadingComplete={() => setImageError(false)}
                    />
                  )
                ) : mediaItem.type === 'video' ? (
                  <video
                    src={imageUrl || mediaItem.url}
                    controls
                    className="max-h-64 rounded"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="text-gray-400">File Preview</div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              {/* Form Fields */}
              <div className="space-y-4">
                {/* Filename */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filename (Image Name)
                  </label>
                  <input
                    type="text"
                    value={formData.filename}
                    onChange={(e) => handleInputChange('filename', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter filename"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Changing the filename will rename the file on the server
                  </p>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter title"
                  />
                </div>

                {/* Caption */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Caption
                  </label>
                  <textarea
                    value={formData.caption}
                    onChange={(e) => handleInputChange('caption', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter caption"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter description"
                  />
                </div>

                {/* Alt Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alt Text
                  </label>
                  <input
                    type="text"
                    value={formData.altText}
                    onChange={(e) => handleInputChange('altText', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter alt text for accessibility"
                  />
                </div>


                {/* File Info */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">File Size:</span>
                      <span className="ml-2 text-gray-900">
                        {(mediaItem.size / 1024).toFixed(2)} KB
                      </span>
                    </div>
                    {mediaItem.dimensions && (
                      <div>
                        <span className="text-gray-500">Dimensions:</span>
                        <span className="ml-2 text-gray-900">
                          {mediaItem.dimensions.width} × {mediaItem.dimensions.height}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Type:</span>
                      <span className="ml-2 text-gray-900 capitalize">{mediaItem.type}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Uploaded:</span>
                      <span className="ml-2 text-gray-900">
                        {new Date(mediaItem.uploadedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={onBack || onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}


